import React, { ReactNode } from 'react'

import Alert from 'components_new/atoms/Alert'
import Box from 'components_new/atoms/Box'
import Link from 'components_new/atoms/Link'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'

import InfoButton from 'components_new/molecules/InfoButton'

import { formatCostUsd } from 'components_new/organisms/internal/DataRuns/utils'

import {
  countJobs,
  estimateRow,
  formatEstimateMinutes,
  formatJobScope,
  RerunRow,
  sumOrNull
} from './lib'

const INCIDENT_PROCESS_PATH = '/internal/data-health/incident-process'

const DESCRIPTION_MAX_LENGTH = 500

interface RerunConfirmStepProps {
  description: string
  onDescriptionChange: (description: string) => void
  /** The selected customers in the order they run. */
  rows: RerunRow[]
  windowSize: number
}

/** Customer minutes spread over the window, never below the longest one. */
function estimateTotalMinutes(rows: RerunRow[], windowSize: number) {
  const minutes = rows.map(
    (row) => estimateRow(row.candidate, row.jobNames).minutes
  )
  const sum = sumOrNull(minutes)

  if (sum === null) {
    return null
  }

  const parallel = Math.max(1, Math.min(windowSize, rows.length))
  const longest = Math.max(...minutes.map((value) => value ?? 0))

  return Math.max(Math.ceil(sum / parallel), Math.ceil(longest))
}

const WINDOW_INFO =
  'Fönstret är ett tak: drivaren startar aldrig fler kunder än att pågående ' +
  'Glue-körningar håller sig under DPU-budgeten (400 av kontots kvot 500). ' +
  '8 ryms under budgeten med dagens SIM- och option_values-storlekar.'

const Fact = (props: { info?: ReactNode; label: string; value: ReactNode }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      px: 2,
      py: 1.5
    }}
  >
    <Text
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}
      variant="caption"
    >
      {props.label}
    </Text>
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
      <Text sx={{ fontWeight: 600 }} variant="body1">
        {props.value}
      </Text>
      {props.info ? <InfoButton sx={{ p: 0 }} title={props.info} /> : null}
    </Box>
  </Box>
)

const OrderItem = (props: { row: RerunRow }) => {
  const { row } = props
  const { candidate } = row

  const notes = [
    formatJobScope(
      row.jobNames.length,
      candidate.collection_jobs.length,
      candidate.downstream_jobs.length
    ),
    candidate.sim_running ? 'väntar in pågående körning' : null
  ].filter(Boolean)

  return (
    <Box component="li" sx={{ py: 0.25 }}>
      <Box
        component="span"
        sx={{ alignItems: 'center', display: 'inline-flex', gap: 1 }}
      >
        <Text component="span" variant="body2">
          {candidate.customer_name}
        </Text>
        <Text component="span" sx={{ color: 'text.secondary' }} variant="body2">
          {notes.join(' · ')}
        </Text>
        {row.priority ? <Tag color="warning" label="Går först" /> : null}
      </Box>
    </Box>
  )
}

const RerunConfirmStep = (props: RerunConfirmStepProps) => {
  const { description, onDescriptionChange, rows, windowSize } = props

  const jobCount = countJobs(rows)
  const collectionCount = rows.reduce(
    (sum, row) => sum + row.jobNames.length,
    0
  )
  const downstreamCount = rows.reduce(
    (sum, row) => sum + row.candidate.downstream_jobs.length,
    0
  )
  const windowed = rows.length > windowSize
  const totalMinutes = estimateTotalMinutes(rows, windowSize)
  const totalCost = sumOrNull(
    rows.map((row) => estimateRow(row.candidate, row.jobNames).costUsd)
  )
  const colliding = rows.filter((row) => row.candidate.sim_running)

  const firstWindow = rows.slice(0, windowSize)
  const rest = rows.slice(windowSize)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))'
          }}
        >
          <Fact label="Kunder" value={rows.length} />
          <Fact
            label="Jobb"
            value={
              <>
                {jobCount}{' '}
                <Text
                  component="span"
                  sx={{ color: 'text.secondary', fontWeight: 400 }}
                  variant="caption"
                >
                  {collectionCount} insamling + {rows.length} SIM +{' '}
                  {downstreamCount} nedströms
                </Text>
              </>
            }
          />
          {windowed ? (
            <Fact
              info={WINDOW_INFO}
              label="Fönster"
              value={`${windowSize} kunder åt gången`}
            />
          ) : null}
          <Fact
            label="Uppskattad tid"
            value={
              windowed && totalMinutes !== null
                ? `${formatEstimateMinutes(
                  totalMinutes
                )} vid fönster ${windowSize}`
                : formatEstimateMinutes(totalMinutes)
            }
          />
          <Fact
            label="Uppskattad kostnad"
            value={totalCost === null ? '–' : `ca ${formatCostUsd(totalCost)}`}
          />
        </Box>
        <Text sx={{ color: 'text.secondary' }} variant="caption">
          Tid och kostnad är uppskattade från snittet per jobb i lyckade
          körningar de senaste 30 dagarna.
        </Text>
      </Box>

      {colliding.length > 0 ? (
        <Alert icon={false} severity="warning">
          <Text sx={{ fontWeight: 700 }} variant="body2">
            Pågående körning
          </Text>
          <Text variant="body2">
            {colliding.map((row) => row.candidate.customer_name).join(', ')} kör
            redan. Omkörningen väntar in en pågående SIM-körning innan kundens
            del börjar.
          </Text>
        </Alert>
      ) : null}

      <Box>
        <Text sx={{ fontWeight: 700 }} variant="body2">
          Körordning
        </Text>
        <Box component="ol" sx={{ m: 0, mt: 0.5, pl: 3 }}>
          {firstWindow.map((row) => (
            <OrderItem key={row.candidate.customer_id} row={row} />
          ))}
          {rest.length > 0 ? (
            <Box
              component="li"
              role="separator"
              sx={{ alignItems: 'center', display: 'flex', gap: 1.5, my: 0.75 }}
            >
              <Box sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
              <Text sx={{ color: 'text.secondary' }} variant="caption">
                De {windowSize} första startar direkt, resten när en plats blir
                ledig
              </Text>
              <Box sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
            </Box>
          ) : null}
          {rest.map((row) => (
            <OrderItem key={row.candidate.customer_id} row={row} />
          ))}
        </Box>
      </Box>

      <TextField
        externalLabel="Beskrivning"
        helperText={`${description.length}/${DESCRIPTION_MAX_LENGTH}`}
        inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
        minRows={2}
        multiline
        noGutter
        onChange={(event) => onDescriptionChange(event.target.value)}
        placeholder="Varför körs det om? T.ex. SIM-fix för hyresavier"
        value={description}
      />

      <Text sx={{ color: 'text.secondary' }} variant="body2">
        Gäller omkörningen en incident, följ{' '}
        <Link to={INCIDENT_PROCESS_PATH} underline="always">
          incidentprocessen
        </Link>
        .
      </Text>
    </Box>
  )
}

RerunConfirmStep.displayName = 'RerunConfirmStep'
export default RerunConfirmStep
